import {
  formatearCaptionItem,
  claveEmpresaFase,
} from "src/utils/propuestaItemLabels";

/**
 * Reordena items agrupando por empresa, preservando orden de aparición de
 * cada empresa y orden interno dentro de cada grupo. Espejo del helper backend
 * `ordenarItemsPorEmpresaBD` en `taxticbackend/src/utils/propuestas.js`.
 * Resultado: numeración global secuencial queda contigua por empresa.
 *
 * La clave sale de `claveEmpresaFase`, la misma que usa el builder de HTML para
 * agrupar: si las dos difirieran, el render partiría los grupos distinto de
 * como se numeraron y volverían los saltos dentro de cada empresa.
 */
const ordenarItemsPorEmpresa = (items) => {
  const grupos = new Map();
  for (const item of items ?? []) {
    const key = claveEmpresaFase(item);
    if (!grupos.has(key)) grupos.set(key, []);
    grupos.get(key).push(item);
  }
  return [...grupos.values()].flat();
};

/**
 * Mapea un item del formulario al shape `fase` que consume el builder HTML.
 * Usado en los 4 call sites que renderizan ProposalPreview (creación y
 * edición, layout XL y dialog mobile). Mantiene paridad de campos con
 * `mapItemsBDAFases` del backend.
 */
export const mapItemFormAFase = (item, i) => ({
  numero: i + 1,
  titulo: item.faseTitulo || item.nombreServicio,
  objetivo: item.faseObjetivo,
  actividades: item.faseActividades,
  entregable: item.faseEntregable,
  duracion: item.faseDuracion,
  monto: Number(item.valorConDescuento || item.valorOriginal) || 0,
  montoOriginal: Number(item.valorOriginal) || 0,
  descuento: Number(item.descuento) || 0,
  fechaTerminoDesc: item.fechaTerminoDesc || "",
  tipoMoneda: item.tipoMoneda,
  refCliente: item.refCliente,
  nombreCliente: item.nombreCliente,
  tipoCaption: formatearCaptionItem(item),
  tipoItem: item.tipoItem,
  tipoFrecuencia: item.tipoFrecuencia,
  refTipoPlan: item.refTipoPlan,
  fechaLimite: item.fechaLimite,
});

/**
 * Aplica el reorden por empresa + map al shape de fase. Usar en los call sites
 * de preview para mantener paridad con el PDF generado en backend.
 */
export const mapItemsFormAFases = (items) =>
  ordenarItemsPorEmpresa(items).map(mapItemFormAFase);

/**
 * Construye el shape que ProposalPreview espera a partir de los values de Formik.
 * Compartido entre FormularioPropuestaV2 (creación) y EditarPropuestaV2 (edición).
 *
 * El shape sigue el contrato del builder de HTML que ambos consumidores usan:
 * campos textuales narrativos, datos del solicitante, datos de contacto del
 * vendedor, e id de la propuesta para referencias en la contraportada
 * ("Propuesta #N").
 */
export const buildPropuestaPreview = (values) => ({
  id: values.id ?? null,
  titulo: values.titulo,
  descripcion: values.descripcion,
  antecedentes: values.antecedentes,
  alcance: values.alcance,
  resumenEjecutivo: values.resumenEjecutivo,
  introduccion: values.introduccion,
  condicionesPago: values.condicionesPago,
  duracionEstimada: values.duracionEstimada,
  proximosPasos: values.proximosPasos,
  clausulaConfidencialidad: values.clausulaConfidencialidad,
  fecha: values.fecha,
  vendedorCorreo: values.vendedorCorreo,
  vendedorTelefono: values.vendedorTelefono,
  nombreSolicitante:
    values.solicitanteData?.nombre || values.solicitanteNuevo?.nombre || "",
  correoSolicitante:
    values.solicitanteData?.correo || values.solicitanteNuevo?.correo || "",
  telefonoSolicitante:
    values.solicitanteData?.telefono || values.solicitanteNuevo?.telefono || "",
});
